<script lang="ts">
	import {
		Alert,
		Button,
		Container,
		Icon,
		Input,
		Popover,
		Select,
		Text,
		toast,
		type SelectOptionType,
	} from '@Shelly/shelly-design-system/index';
	import { validate_email } from 'src/helpers/validators';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { sub_units } from 'src/stores/account/home';
	import { addSubUnit, changeSubUnitRights } from 'src/services/iotu/house-management';
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	import type { SubUnitData, SubUnitMeta, SubUnitRights } from './interfaces/SubUnit';
	import { format } from 'svelte-i18n';

	const dispatch = createEventDispatcher<{
		closeModal: { sub: string; meta: SubUnitMeta; rights: string } | null;
	}>();

	export let subUnitToEdit: Record<string, SubUnitData> | null = null;

	let isLoading = false;
	let controlPopover = false;
	let settingsPopover = false;
	let runScenesPopover = false;
	let enableScenesPopover = false;
	let roomsPopover = false;
	let isFormDirty = false;
	let responseError: string | null = null;
	let translations: Record<string, string> = {};

	let email = '';
	let isEmailReadonly = false;
	let emailError = '';

	let name = '';
	let isNameReadonly = false;
	let nameError = '';

	let options = [
		{ title: 'Full access', value: ['*'] },
		{ title: 'No access', value: [] },
	];
	const permissions = {
		control: ['*'] as string[],
		settings: ['*'] as string[],
		runScenes: ['*'] as string[],
		toggleScenes: ['*'] as string[],
		rooms: ['*'] as string[],
	};

	function extractSubId(key: string): string {
		return key.replace(/^sub_/, '');
	}

	onMount(() => {
		setTranslations();
		if (subUnitToEdit) {
			prepareEdit();
		} else {
			isFormDirty = true;
		}
	});

	onDestroy(() => {
		cleanUp();
	});

	function setTranslations(): void {
		translations.editButton = $format('button_save_settings', {
			default: 'Save Settings',
		});
		translations.createButton = $format('shared_invite_sub_unit', { default: 'Invite to Home' });
		translations.updatedToast = $format('shared_sub_unit_update_toaster', {
			default: 'Home access updated.',
		});
		translations.createSuccessToast = $format('shared_sub_unit_create_toaster', {
			default: 'The invitation has been sent.',
		});
		translations.nameError = $format('settings_sub_unit_create_name_error', {
			default: 'Name is not valid. Please enter a valid name.',
		});
		translations.emailError = $format('settings_sub_unit_create_email_error', {
			default: 'Email is not valid. Please enter a valid email.',
		});
		options[0].title = $format('settings_sub_unit_create_full_access', { default: 'Full access' });
		options[1].title = $format('settings_sub_unit_create_no_access', { default: 'No access' });
	}

	function onPermissionChange(
		option: keyof typeof permissions,
		event: CustomEvent<SelectOptionType>,
	): void {
		isFormDirty = true;
		permissions[option] = (event.detail as any).value;
	}

	async function onSubmitClick(): Promise<void> {
		if (!validateForm()) return;

		if (subUnitToEdit) {
			await handleRightsEdit();
			return;
		}

		await handleCreate();
	}

	async function handleCreate(): Promise<void> {
		const userData = get(user_data);
		const meta: SubUnitMeta = {
			email,
			lang: userData.lang,
			tz: userData.timezone,
			nperm: buildNotificationPermissions(),
			name,
			avatar: 'color-green',
		};
		const rights: SubUnitRights = [
			permissions.control,
			permissions.settings,
			permissions.runScenes,
			permissions.toggleScenes,
			permissions.rooms,
		];

		isLoading = true;
		responseError = null;

		try {
			const response = await addSubUnit(meta, rights);

			if (!response.isok) {
				const message = (response.errors as { message?: string })?.message;
				throw new Error(
					/already shared/i.test(message ?? '')
						? $format('shared_sub_unit_already_shared', {
								default: 'This account is already shared.',
							})
						: (message ?? $format('error_global', { default: 'Something went wrong.' })),
				);
			}

			dispatch('closeModal', {
				sub: response.data?.sub,
				meta,
				rights: JSON.stringify(rights),
			});

			toast.open({
				title: translations.createSuccessToast,
				type: 'success',
				duration: 2000,
			});
		} catch (err: any) {
			responseError = err.message;
		} finally {
			isLoading = false;
		}
	}

	async function handleRightsEdit(): Promise<void> {
		const id = Object.keys(subUnitToEdit)[0];
		const sub = extractSubId(id);
		const rights: SubUnitRights = [
			permissions.control,
			permissions.settings,
			permissions.runScenes,
			permissions.toggleScenes,
			permissions.rooms,
		];

		isLoading = true;
		responseError = null;

		try {
			const response = await changeSubUnitRights(sub, rights);

			if (!response.isok) {
				throw new Error((response.errors as any)?.message ?? 'Failed to update rights');
			}

			const meta = Object.values(subUnitToEdit)[0][0];
			dispatch('closeModal', { sub, meta, rights: JSON.stringify(rights) });

			toast.open({
				title: translations.updatedToast,
				type: 'success',
				duration: 2000,
			});
		} catch (err: any) {
			responseError = err.message;
		} finally {
			isLoading = false;
		}
	}

	function prepareEdit(): void {
		const id = Object.keys(subUnitToEdit)[0];
		const subUnits = get(sub_units);
		const entry = Object.entries(subUnits).find(([key]) => key === id);

		if (!entry) {
			toast.open({
				title: $format('error_global', { default: 'Something went wrong.' }),
				type: 'danger',
				duration: 2000,
			});
			return;
		}

		const data = entry[1];
		email = data[0].email;
		name = data[0].name;
		permissions.control = data[1];
		permissions.settings = data[2];
		permissions.runScenes = data[3];
		permissions.toggleScenes = data[4];
		permissions.rooms = data[5] ?? ['*'];

		isEmailReadonly = true;
		isNameReadonly = true;
	}

	function buildNotificationPermissions(): string {
		const userSettings = get(user_settings);
		const parts: string[] = [];

		if (userSettings.push_notifications_permissions) parts.push('P');
		if (userSettings.mail_notifications_permissions) parts.push('M');

		return parts.join('');
	}

	function validateForm(): boolean {
		let isValid = true;

		emailError = '';
		nameError = '';

		if (name.length === 0) {
			nameError = translations.nameError;
			isValid = false;
		}

		if (!validate_email(email)) {
			emailError = translations.emailError;
			isValid = false;
		}

		return isValid;
	}

	function cleanUp(): void {
		subUnitToEdit = null;
		responseError = null;
		email = '';
		name = '';
		permissions.control = ['*'];
		permissions.settings = ['*'];
		permissions.runScenes = ['*'];
		permissions.toggleScenes = ['*'];
		permissions.rooms = ['*'];
	}

	function getSelectOptionTypeByPermission(permission: string[]): SelectOptionType {
		return options.find((o) => o.value.length === permission.length);
	}
</script>

<Container slot="content" padding="scrollPaddings">
	{#if !subUnitToEdit}
		<Alert
			class="mb-m"
			heading={$format('settings_sub_unit_create_alert_header', {
				default:
					'An invite will be sent to the entered email address with a link to join your home.',
			})}
		/>
	{/if}
	{#if responseError}
		<Alert
			class="mb-m"
			heading={$format('alert_danger_error_heading', { default: 'Error' })}
			message={responseError}
			type="danger"
		/>
	{/if}
	<Input
		class="mb-m"
		type="text"
		label={$format('label_name', {
			default: 'Name',
		})}
		disabled={isNameReadonly}
		error={nameError}
		bind:value={name}
		id="input-subunit-name"
		aria-labelledby="input-subunit-name"
	/>
	<Input
		class="mb-m"
		type="email"
		label={$format('settings_sub_unit_create_label_email', { default: 'Email' })}
		disabled={isEmailReadonly}
		error={emailError}
		bind:value={email}
		id="input-subunit-email"
		aria-labelledby="input-subunit-email"
	/>

	<Text element="p" styleType="body-small" class="mb-s"
		>{$format('settings_sub_unit_create_label_control_devices', {
			default: 'Control Devices',
		})}
	</Text>
	<Select
		aria-label={$format('select_control_devices', { default: 'Select control devices' })}
		class="mb-m"
		{options}
		value={getSelectOptionTypeByPermission(permissions.control)}
		icon="fas fa-user"
		on:change={(event) => onPermissionChange('control', event)}
		id="select-subunit-control"
	/>

	<Text element="p" styleType="body-small" class="mb-s"
		>{$format('settings_sub_unit_create_label_control_devices_settings', {
			default: 'Allow devices settings access',
		})}
	</Text>
	<Select
		aria-label={$format('select_device_settings', { default: 'Select device settings' })}
		class="mb-m"
		{options}
		value={getSelectOptionTypeByPermission(permissions.settings)}
		icon="fas fa-user"
		on:change={(event) => onPermissionChange('settings', event)}
		id="select-subunit-settings"
	/>

	<Text element="p" styleType="body-small" class="mb-s"
		>{$format('settings_sub_unit_create_label_control_scenes', {
			default: 'Allow to run scenes',
		})}
	</Text>
	<Select
		aria-label={$format('select_run_scenes', { default: 'Select run scenes' })}
		class="mb-m"
		{options}
		value={getSelectOptionTypeByPermission(permissions.runScenes)}
		icon="fas fa-user"
		on:change={(event) => onPermissionChange('runScenes', event)}
		id="select-subunit-run-scenes"
	/>

	<Text element="p" styleType="body-small" class="mb-s"
		>{$format('settings_sub_unit_create_label_toggle_scenes', {
			default: 'Allow to enable/disable scenes',
		})}
	</Text>
	<Select
		aria-label={$format('select_toggle_scenes', { default: 'Select toggle scenes' })}
		class="mb-m"
		{options}
		value={getSelectOptionTypeByPermission(permissions.toggleScenes)}
		icon="fas fa-user"
		on:change={(event) => onPermissionChange('toggleScenes', event)}
		id="select-subunit-toggle-scenes"
	/>

	<Button
		class="mb-m"
		variant="primary"
		size="m"
		loading={isLoading}
		disabled={!isFormDirty}
		on:click={onSubmitClick}
		>{subUnitToEdit ? translations.editButton : translations.createButton}
	</Button>
</Container>
